import { periodLabel } from './financialYearWindow';

/**
 * The Water Management Dashboard's derived figures.
 *
 * Four numbers on that page are computed rather than measured: no meter
 * reports KIL, total canteen consumption, or the STP split. They lived inline
 * in four near-identical chart components, which is how a find-and-replace
 * across the file once silently rewrote one of them. They are pure functions
 * here so the calculation can be read and tested without a chart around it.
 *
 * Each takes widget rows and returns widget rows, so the chart components stay
 * unaware that anything was derived.
 */

/** KPI names the calculations reference, as the backend spells them. */
export const Kpi = {
  ADMIN_TOP: 'From Borewell to Admin',
  CANTEEN_GES: 'Canteen + GES',
  AWANTHI_50: 'Awanthi 50 ppm',
  GES: 'GES',
  ETP: 'ETP',
  ETP_UNTREATED: 'ETP (Untreated)',
  FINAL_TREATED: 'Final Treated (STP + ETP)',
  STP_UNTREATED: 'STP (Untreated)',
};

/** Names given to series no meter reports. */
export const Derived = {
  KIL: 'KIL',
  TOTAL_CANTEEN: 'Total Canteen',
  STP_TREATED: 'STP Treated',
  STP_UNTREATED: 'STP Untreated',
  ETP_TREATED: 'ETP Treated',
  ETP_UNTREATED: 'ETP Untreated',
};

/**
 * Groups widget rows by their display period.
 *
 * Insertion order is kept, because the rows arrive ordered by period and the
 * charts read them in that order.
 *
 * The period's timestamp is carried alongside its values, because the charts
 * pivot on `period_ts` rather than on the display label — a derived row that
 * kept only the label would plot nothing.
 *
 * @param {Array<object>} rows - Widget rows carrying `displayPeriods` and `kpi`.
 * @returns {Map<string, {ts: string, kpis: object}>} Period label to its
 *   timestamp and a map of KPI name to `{value, unit}`.
 */
const groupByPeriod = (rows = []) => {
  const periods = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.displayPeriods) return;

    if (!periods.has(row.displayPeriods)) {
      periods.set(row.displayPeriods, { ts: row.period_ts, kpis: {} });
    }

    periods.get(row.displayPeriods).kpis[row.kpi] = {
      value: Number(row.sum_value) || 0,
      unit: row.unit,
    };
  });

  return periods;
};

/**
 * Reads a KPI's value for a period, treating an absent KPI as zero.
 * @param {object} period - One period's KPI map.
 * @param {string} kpi - KPI to read.
 * @returns {number} The value, or 0 when that KPI did not report.
 */
const valueOf = (period, kpi) => period[kpi]?.value ?? 0;

/**
 * Splits borewell consumption between the metered locations and the remainder.
 *
 * `KIL = From Borewell to Admin - (GES + Sports Club)` — everything that
 * reached the admin top and was not accounted for by a metered location. The
 * remainder absorbs every error upstream of it, which is why it can only be
 * calculated when the admin top itself reported: without that total there is
 * nothing to take the metered locations out of, and a remainder computed from
 * a missing total would read as zero consumption rather than as no reading.
 *
 * @param {Array<object>} rows - Widget rows stacked by KPI.
 * @returns {Array<object>} Rows for each metered location plus KIL, per period.
 */
export const locationConsumption = (rows = []) => {
  const derived = [];

  groupByPeriod(rows).forEach(({ ts, kpis }, displayPeriods) => {
    const metered = Object.keys(kpis).filter(
      (kpi) => kpi !== Kpi.ADMIN_TOP && kpi !== Kpi.CANTEEN_GES
    );

    metered.forEach((kpi) => {
      derived.push({
        period_ts: ts,
        displayPeriods,
        kpi,
        sum_value: kpis[kpi].value,
        unit: kpis[kpi].unit,
      });
    });

    if (!kpis[Kpi.ADMIN_TOP]) return;

    const remainder = metered.reduce(
      (total, kpi) => total - kpis[kpi].value,
      kpis[Kpi.ADMIN_TOP].value
    );

    derived.push({
      period_ts: ts,
      displayPeriods,
      kpi: Derived.KIL,
      sum_value: Math.max(0, remainder),
      unit: kpis[Kpi.ADMIN_TOP].unit,
    });
  });

  return derived;
};

/**
 * Canteen consumption, which no single meter reports.
 *
 * `Canteen Consumption = 11 Canteen + Awanthi 50 ppm - GES` — the canteen
 * shares a feed with GES, so GES comes back out of the combined reading.
 *
 * @param {Array<object>} rows - Widget rows stacked by KPI.
 * @returns {Array<object>} One row per period carrying the canteen total.
 */
export const canteenConsumption = (rows = []) => {
  const derived = [];
  const unit = rows?.[0]?.unit;

  groupByPeriod(rows).forEach(({ ts, kpis }, displayPeriods) => {
    const total =
      valueOf(kpis, Kpi.CANTEEN_GES) +
      valueOf(kpis, Kpi.AWANTHI_50) -
      valueOf(kpis, Kpi.GES);

    derived.push({
      period_ts: ts,
      displayPeriods,
      kpi: Derived.TOTAL_CANTEEN,
      sum_value: Math.max(0, total),
      unit,
    });
  });

  return derived;
};

/**
 * Sets what the sewage plant treated against what arrived at it.
 *
 * Only the combined treated figure is metered, so the plant's own share is
 * what is left after the effluent plant's is taken out. The inlet is metered
 * directly.
 *
 * The two series carried each other's names until now: the subtraction — which
 * is treated water — was labelled 'STP (Untreated)', and the untreated inlet
 * reading was labelled 'STP'. The chart therefore showed treated water under
 * the untreated name and the reverse, which is the opposite of what it was
 * drawn to show.
 *
 * These labels are the series names on the chart, not KPI names: the request
 * still asks for the KPIs the backend knows, and only the rows this returns
 * are named for the reader.
 *
 * @param {Array<object>} rows - Widget rows stacked by KPI.
 * @returns {Array<object>} Two rows per period, one per series.
 */
export const stpTreatment = (rows = []) => {
  const derived = [];
  const unit = rows?.[0]?.unit;

  groupByPeriod(rows).forEach(({ ts, kpis }, displayPeriods) => {
    derived.push({
      period_ts: ts,
      displayPeriods,
      kpi: Derived.STP_TREATED,
      sum_value: Math.max(
        0,
        valueOf(kpis, Kpi.FINAL_TREATED) - valueOf(kpis, Kpi.ETP)
      ),
      unit,
    });

    derived.push({
      period_ts: ts,
      displayPeriods,
      kpi: Derived.STP_UNTREATED,
      sum_value: valueOf(kpis, Kpi.STP_UNTREATED),
      unit,
    });
  });

  return derived;
};

/**
 * Names the effluent plant's two series for what they are.
 *
 * Both figures are metered — the inlet and the outlet — so nothing is
 * calculated here. What the chart drew were the backend's own KPI names, "ETP
 * (Untreated)" and a bare "ETP", under a title promising treated against
 * untreated. The reader had to know that the bare one meant the outlet.
 *
 * The KPI strings in the request are untouched; only the rows this returns are
 * named for a reader.
 *
 * @param {Array<object>} rows - Widget rows stacked by KPI.
 * @returns {Array<object>} The same rows, with the two series named.
 */
export const etpTreatment = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    kpi:
      {
        [Kpi.ETP_UNTREATED]: Derived.ETP_UNTREATED,
        [Kpi.ETP]: Derived.ETP_TREATED,
      }[row.kpi] || row.kpi,
  }));

/**
 * Volume the STP treated, for the summary card.
 *
 * The card compares the two most recent days the plant actually treated
 * anything. A day the calculation yields zero is skipped rather than compared
 * against, because the gap-filled response reports a day nobody reported in as
 * zero, and treating that as a real reading makes the change meaningless.
 *
 * @param {Array<object>} rows - Widget rows carrying `period_ts` and `kpi`.
 * @returns {{latest: object, previous: object|null}|null} The two most recent
 *   treating days, or null when the plant treated nothing in the window.
 */
export const stpTreatedDays = (rows = []) => {
  const byDay = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.period_ts) return;

    if (!byDay.has(row.period_ts)) byDay.set(row.period_ts, {});
    byDay.get(row.period_ts)[row.kpi] = { value: Number(row.sum_value) || 0 };
  });

  const treating = Array.from(byDay.entries())
    .map(([periodTs, period]) => ({
      periodTs,
      value: Math.max(
        0,
        valueOf(period, Kpi.FINAL_TREATED) - valueOf(period, Kpi.ETP)
      ),
    }))
    .filter((day) => day.value !== 0)
    .sort((a, b) => new Date(b.periodTs) - new Date(a.periodTs));

  if (!treating.length) return null;

  return { latest: treating[0], previous: treating[1] || null };
};

/**
 * The STP card's figures, in the shape the other seven cards use.
 *
 * `comparePeriods` reads a value straight off a row, and this one has to be
 * computed from two KPIs first, so the card would otherwise need a second
 * branch for its own arithmetic. Handing back the same shape keeps one card.
 *
 * @param {Array<object>} rows - Widget rows carrying `period_ts` and `kpi`.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {object|null} Values, change and labels, or null when the plant
 *   treated nothing in the window.
 */
export const stpTreatedComparison = (rows = [], now) => {
  const days = stpTreatedDays(rows);
  if (!days) return null;

  const latestValue = days.latest.value;
  const previousValue = days.previous?.value ?? 0;
  const changeValue = latestValue - previousValue;

  return {
    latestValue,
    previousValue,
    changeValue,
    change: previousValue ? (changeValue / previousValue) * 100 : 0,
    latestLabel: periodLabel({ period_ts: days.latest.periodTs }, now),
    previousLabel: days.previous
      ? periodLabel({ period_ts: days.previous.periodTs }, now)
      : 'No data',
  };
};
